import React, { useEffect, useState } from "react";
import "./index.css";
import { BrandBadge } from "./components/brand";
import TallyFormEmbed from "./components/tally";

function App() {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // TODO: Replace with actual API endpoint
    setTimeout(() => {
      setIsSubmitted(true);
      setIsLoading(false);

      // Track conversion in GA
      if (window.gtag) {
        window.gtag('event', 'signup_interest', {
          event_category: 'engagement',
          event_label: email
        });
      }
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Navigation */}
      <nav className="container mx-auto px-6 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BrandBadge fontSize={28} />
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <main className="container mx-auto px-6 py-12 md:py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <div className="space-y-8">
            <div className="inline-block">
              <span className="bg-gradient-to-r from-[#FF9800] to-[#FF5722] text-white px-4 py-2 rounded-full text-sm font-semibold">
                🚀 Coming Soon to NYC
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold leading-tight">
              Find Your Crew,
              <br />
              <span className="bg-gradient-to-r from-[#FF9800] to-[#FF5722] bg-clip-text text-transparent">
                Build Your Hustle
              </span>
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed">
              MadeLocal connects you with locals to start businesses, learn skills,
              and build something that's actually yours.
            </p>


            {/* Stats */}
            <div className="flex flex-wrap gap-8 pt-4">
              <div>
                <div className="text-3xl font-bold text-gray-900">NYC</div>
                <div className="text-sm text-gray-600">Starting Local</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">5</div>
                <div className="text-sm text-gray-600">Boroughs</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-gray-900">Free</div>
                <div className="text-sm text-gray-600">Forever</div>
              </div>
            </div><div className="flex justify-left">
              <div className="w-full max-w-md">
                <TallyFormEmbed />
              </div>
            </div>


          </div>

          {/* Right Column */}
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-6">

              <div className="space-y-4">
                <FeatureCard
                  icon="🐕"
                  title="Real Examples"
                  description="Baker + marketer. Plumber + apprentice. Two cleaners. Find a partner and start something."
                  color="from-orange-500 to-orange-600"
                />
                <FeatureCard
                  icon="🤝"
                  title="You Figure It Out"
                  description="How you split money, time, and work is between you and your team. We just make the introduction."
                  color="from-blue-500 to-blue-600"
                />
                <FeatureCard
                  icon="🚀"
                  title="Start Small, Grow Together"
                  description="Weekend side hustle or full-time business. Start in your neighborhood, scale if it works."
                  color="from-purple-500 to-purple-600"
                />
              </div>

              <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-orange-500">
                <p className="font-semibold mb-2 text-orange-500">
                  Look, not everyone can be an Uber driver or the next Sophie Rain.
                </p>
                <p className="text-sm text-gray-600">
                  But you <b>CAN</b> team up with someone in your neighborhood and build something real --
                  Business partners. Mentor and apprentice. Start a local service. Whatever actually works for you and your team.
                </p>
              </div>
            </div>

            <div className="absolute -top-4 -right-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-full shadow-lg transform rotate-12">
              <span className="font-bold">Pre-Launch</span>
            </div>
          </div>
        </div>

        {/* How It Works Section */}
        <div className="mt-32">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            How It Works
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            <StepCard
              number="1"
              title="Sign Up"
              description="Tell us your skills, what you want to learn or mentor, and where in NYC you are"
            />
            <StepCard
              number="2"
              title="Match with Locals"
              description="Find people in your neighborhood with complementary skills and goals"
            />
            <StepCard
              number="3"
              title="Start Building"
              description="Form teams, start projects, learn trades, build income"
            />
          </div>
        </div>

        {/* Who It's For Section */}
        <div className="mt-32 bg-white rounded-3xl shadow-xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Is This For You?
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <PersonaCard
              emoji="🚗"
              title="Gig Workers & Drivers"
              description="Tired of the algorithm? Want to learn plumbing, electrical, HVAC and build something more stable?"
            />
            <PersonaCard
              emoji="💼"
              title="Office Workers"
              description="Bring your business skills to trade teams, or learn hands-on work from scratch. Either way, you're part of the crew."
            />
            <PersonaCard
              emoji="🔧"
              title="Trade Providers"
              description="Help your community and be a mentor while growing your own team and business."
            />
            <PersonaCard
              emoji="🏠"
              title="Service Workers"
              description="Cleaners, movers, caregivers wanting to team up and build your own thing."
            />
          </div>
        </div>
        {/* Reality Check Section */}
        <div className="mt-32 bg-gradient-to-r from-[#FF9800] to-[#FF5722] rounded-3xl p-12 text-white">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            The Real Talk
          </h2>

          <p className="text-center text-xl text-white/90 mb-12 max-w-2xl mx-auto">
            No fake success stories. Here's what this actually is:
          </p>
          <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="text-3xl mb-3">🚀</div>
              <h3 className="font-bold text-xl mb-2">We're Brand New</h3>
              <p className="text-white/90">
                Zero users right now. You'd be one of the first to build this with us.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all">
              <div className="text-3xl mb-3">🗺️</div>
              <h3 className="font-bold text-xl mb-2">Built for NYC</h3>
              <p className="text-white/90">
                Meet face-to-face. Build local businesses. Serve your city. You decide how far you'll travel.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all">
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="font-bold text-xl mb-2">Any Hustle Works</h3>
              <p className="text-white/90">
                Dog treats, cleaning, repairs, tutoring, crafts — if two people can build it together, we're for it.<br />
                <small>* Obviously certain industries need licensing</small>
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all">
              <div className="text-3xl mb-3">💰</div>
              <h3 className="font-bold text-xl mb-2">Always Free</h3>
              <p className="text-white/90">
                No fees to join. No cuts of what you make. We might charge businesses later, but regular people? Free forever.
              </p>
            </div>
          </div>

          <p className="text-center text-xl font-semibold mt-12">
            If that sounds real to you, join the waitlist.
          </p>
        </div>

        {/* Final CTA */}
        <div className="mt-32 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Sign Up to the Waiting List!
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            No BS. No hidden fees. Just people helping people. 
            
          </p><div className="flex justify-center">
            <div className="w-full max-w-md">
              <TallyFormEmbed />
            </div>
          </div>


        </div>
      </main>

      {/* Footer */}
      <footer className="container mx-auto px-6 py-12 mt-24 border-t border-gray-200">
        <div className="text-center text-gray-600">
          <p className="mb-2">© 2026 MadeLocal. Let's build NYC 🗽 together.</p>
        </div>
      </footer>
    </div>
  );
}

// Component: Feature Card
function FeatureCard({
  icon,
  title,
  description,
  color
}: {
  icon: string;
  title: string;
  description: string;
  color: string;
}) {
  return (
    <div className="flex items-start space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
      <div className={`w-12 h-12 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center text-2xl flex-shrink-0`}>
        {icon}
      </div>
      <div>
        <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  );
}

// Component: Step Card
function StepCard({
  number,
  title,
  description
}: {
  number: string;
  title: string;
  description: string;
}) {
  return (
    <div className="text-center">
      <div className="w-16 h-16 bg-gradient-to-br from-[#FF9800] to-[#FF5722] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
        {number}
      </div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

// Component: Persona Card
function PersonaCard({
  emoji,
  title,
  description
}: {
  emoji: string;
  title: string;
  description: string;
}) {
  return (
    <div className="p-6 rounded-xl border-2 border-gray-200 hover:border-[#FF9800] transition-colors">
      <div className="text-5xl mb-3">{emoji}</div>
      <h3 className="font-semibold text-gray-900 mb-2 text-xl">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

// Extend Window type for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export default App;